import { useState } from "react";
import { useNavigate } from "react-router-dom";
import { useQuery, useMutation } from "@tanstack/react-query";
import { CATEGORIES, type Category } from "@connect/shared";
import { useApi } from "@/lib/api";
import { Button } from "@/components/ui/button";
import { cn } from "@/lib/cn";

interface CommunityRow {
  id: string;
  slug: string;
  name: string;
  tagline: string | null;
}

type Pillars = { mind: number; body: number; soul: number; lifestyle: number };

const PILLAR_LABELS: Record<keyof Pillars, string> = {
  mind: "Mind — focus, leadership, psychology",
  body: "Body — health, fitness, recovery",
  soul: "Soul — faith, purpose, meaning",
  lifestyle: "Lifestyle — career, money, opportunity",
};

const CATEGORY_LABELS: Partial<Record<Category, string>> = {
  identity: "Identity",
  brotherhood: "Brotherhood",
  sisterhood: "Sisterhood",
  wellness: "Wellness",
  faith: "Faith",
  entrepreneurship: "Entrepreneurship",
  workforce: "Workforce",
  policy: "Policy",
  leadership: "Leadership",
  economic_mobility: "Economic Mobility",
  technology: "Technology",
  education: "Education",
  athlete_development: "Athletes",
  investments: "Investments",
  grants: "Grants",
  culture: "Culture",
};

export function Onboarding() {
  const api = useApi();
  const navigate = useNavigate();
  const communitiesQ = useQuery({
    queryKey: ["communities"],
    queryFn: () => api.get<{ communities: CommunityRow[] }>("/communities"),
  });

  const [step, setStep] = useState<0 | 1 | 2 | 3 | 4>(0);
  const [displayName, setDisplayName] = useState("");
  const [primaryCommunityId, setPrimaryCommunityId] = useState<string>("");
  const [city, setCity] = useState("");
  const [goals, setGoals] = useState<string[]>([]);
  const [goalDraft, setGoalDraft] = useState("");
  const [pillars, setPillars] = useState<Pillars>({
    mind: 0.5,
    body: 0.5,
    soul: 0.5,
    lifestyle: 0.5,
  });
  const [interests, setInterests] = useState<Category[]>([]);

  const submit = useMutation({
    mutationFn: () =>
      api.post("/onboarding/complete", {
        displayName,
        primaryCommunityId,
        city: city || undefined,
        goals,
        pillars,
        interests,
      }),
    onSuccess: () => navigate("/app"),
  });

  const canNext =
    (step === 0 && displayName.trim().length > 0) ||
    (step === 1 && !!primaryCommunityId) ||
    (step === 2 && goals.length > 0) ||
    (step === 3 && true) ||
    (step === 4 && interests.length > 0);

  return (
    <div className="min-h-dvh flex items-center justify-center px-6 py-12">
      <div className="w-full max-w-xl">
        <div className="mb-10 flex items-center gap-2 text-xs text-whisper tabular-nums">
          <span className="text-gold-glow">{step + 1}</span>
          <span className="text-muted">/ 5</span>
          <div className="flex-1 ml-4 h-px bg-veil/60 relative overflow-hidden">
            <div
              className="absolute inset-y-0 left-0 bg-gold transition-all"
              style={{ width: `${((step + 1) / 5) * 100}%` }}
            />
          </div>
        </div>

        {step === 0 && (
          <Stage eyebrow="Welcome" title="What should we call you?">
            <input
              type="text"
              autoFocus
              value={displayName}
              onChange={(e) => setDisplayName(e.target.value)}
              placeholder="Your name"
              className="w-full bg-transparent border-b border-veil/80 pb-3 text-2xl font-display focus:outline-none focus:border-gold transition-colors"
            />
          </Stage>
        )}

        {step === 1 && (
          <Stage eyebrow="Ecosystem" title="Where do you belong?">
            <div className="grid gap-3 mt-6">
              {communitiesQ.data?.communities.map((c) => (
                <button
                  key={c.id}
                  type="button"
                  onClick={() => setPrimaryCommunityId(c.id)}
                  className={cn(
                    "panel p-5 text-left transition-colors",
                    primaryCommunityId === c.id
                      ? "border-gold/60 shadow-gold-glow"
                      : "hover:border-veil",
                  )}
                >
                  <div className="font-display text-lg">{c.name}</div>
                  {c.tagline && <div className="text-sm text-whisper mt-1">{c.tagline}</div>}
                </button>
              ))}
            </div>
          </Stage>
        )}

        {step === 2 && (
          <Stage eyebrow="Aspirations" title="What are you building toward?">
            <div className="mt-6 space-y-3">
              <div className="flex gap-2">
                <input
                  value={goalDraft}
                  onChange={(e) => setGoalDraft(e.target.value)}
                  onKeyDown={(e) => {
                    if (e.key === "Enter" && goalDraft.trim()) {
                      setGoals([...goals, goalDraft.trim()]);
                      setGoalDraft("");
                    }
                  }}
                  placeholder="e.g. launch a fund, become a chapter captain"
                  className="flex-1 bg-graphite/60 border border-veil/60 rounded-lg px-4 py-2.5 text-sm focus:outline-none focus:border-gold/60"
                />
                <Button
                  size="sm"
                  variant="secondary"
                  onClick={() => {
                    if (goalDraft.trim()) {
                      setGoals([...goals, goalDraft.trim()]);
                      setGoalDraft("");
                    }
                  }}
                >
                  Add
                </Button>
              </div>
              <ul className="space-y-1.5">
                {goals.map((g, i) => (
                  <li key={i} className="text-sm text-fog/90 flex items-center gap-2">
                    <span className="text-gold-glow">·</span>
                    {g}
                  </li>
                ))}
              </ul>
              <input
                value={city}
                onChange={(e) => setCity(e.target.value)}
                placeholder="City (optional)"
                className="w-full bg-graphite/60 border border-veil/60 rounded-lg px-4 py-2.5 text-sm focus:outline-none focus:border-gold/60 mt-4"
              />
            </div>
          </Stage>
        )}

        {step === 3 && (
          <Stage eyebrow="Pillars" title="Where do you want to grow?">
            <div className="mt-6 space-y-5">
              {(Object.keys(PILLAR_LABELS) as Array<keyof Pillars>).map((p) => (
                <label key={p} className="block">
                  <div className="flex items-center justify-between mb-2">
                    <span className="text-sm">{PILLAR_LABELS[p]}</span>
                    <span className="text-xs tabular-nums text-whisper">
                      {Math.round(pillars[p] * 100)}
                    </span>
                  </div>
                  <input
                    type="range"
                    min="0"
                    max="100"
                    value={pillars[p] * 100}
                    onChange={(e) =>
                      setPillars({ ...pillars, [p]: Number(e.target.value) / 100 })
                    }
                    className="w-full accent-gold"
                  />
                </label>
              ))}
            </div>
          </Stage>
        )}

        {step === 4 && (
          <Stage eyebrow="Interests" title="What signals matter to you?">
            <div className="mt-6 flex flex-wrap gap-2">
              {CATEGORIES.map((c) => {
                const on = interests.includes(c);
                return (
                  <button
                    key={c}
                    type="button"
                    onClick={() =>
                      setInterests(on ? interests.filter((x) => x !== c) : [...interests, c])
                    }
                    className={cn(
                      "px-3 py-1.5 rounded-full text-xs border transition-colors",
                      on
                        ? "border-gold/60 bg-gold/10 text-gold-glow"
                        : "border-veil/60 text-whisper hover:border-veil hover:text-fog",
                    )}
                  >
                    {CATEGORY_LABELS[c] ?? c}
                  </button>
                );
              })}
            </div>
          </Stage>
        )}

        <div className="mt-10 flex justify-between">
          <Button
            variant="ghost"
            size="sm"
            onClick={() => setStep((s) => Math.max(0, s - 1) as typeof step)}
            disabled={step === 0}
          >
            Back
          </Button>
          {step < 4 ? (
            <Button
              size="md"
              disabled={!canNext}
              onClick={() => setStep((s) => (s + 1) as typeof step)}
            >
              Continue
            </Button>
          ) : (
            <Button
              size="md"
              disabled={!canNext || submit.isPending}
              onClick={() => submit.mutate()}
            >
              {submit.isPending ? "Entering…" : "Enter CONNECT"}
            </Button>
          )}
        </div>
      </div>
    </div>
  );
}

function Stage({
  eyebrow,
  title,
  children,
}: {
  eyebrow: string;
  title: string;
  children: React.ReactNode;
}) {
  return (
    <div className="animate-fade-up">
      <p className="label-eyebrow text-gold-glow">{eyebrow}</p>
      <h2 className="mt-3 text-display-2 font-display">{title}</h2>
      {children}
    </div>
  );
}
